import axios from 'axios'

import type { NotificationData, NotificationType } from '../components/CopNotifications/NotificationContentRender'
import { getErrorMessage } from '../constants/common-error-messages'
import { APP_NOTIFICATION_URL, APPROVE_CASE_ENDPOINT, COP_API_BASE_URL, REJECT_CASE_ENDPOINT, SIGNALR_NEGOTIATE_URL, TOKEN_EXCHANGE_URL } from '../constants/service-endpoints'
import { ToastService } from './toast.service'
import { buildCopHeaders, getSilentToken } from './tokenUtils'

export type NotificationCategory = 'All' | 'Agreements' | 'Cases' | 'Assets'

export interface NotificationListParams {
  category: NotificationCategory
  top?: number
  skip?: number
  unreadOnly?: boolean
}

export interface NotificationAction {
  id: string
  type: string
  isAvailable: boolean
  triggeredBy: string | null
}

export type NotificationActions = NotificationAction[]

export interface AppNotification {
  id: string
  Title: string
  Body: string
  isRead: boolean
  SortDate: string
  category: string
  LinkUrl?: string
  LinkLabel?: string
  createdAt: string
  type: NotificationType
  data?: NotificationData
  actions?: NotificationActions
}

export interface NotificationListResponse {
  '@odata.count'?: number
  value: AppNotification[]
}

export interface AppNotificationCategory {
  id: number
  name: string
}

export interface AppNotificationCategoriesResponse {
  '@odata.context'?: string
  categories: AppNotificationCategory[]
}

export interface SignalRNegotiateResponse {
  negotiateVersion: number
  url: string
  accessToken: string
  availableTransports: string[]
}

function buildODataQuery(top: number, skip: number, unreadOnly?: boolean): string {
  const query = new URLSearchParams()
  if (unreadOnly) query.set('$filter', 'isRead eq false')
  query.set('$orderby', 'Notification/SortDate desc')
  query.set('$count', 'true')
  query.set('$top', String(top))
  query.set('$skip', String(skip))
  return `?${query.toString()}`
}

export async function getCategories(): Promise<AppNotificationCategoriesResponse | null> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${APP_NOTIFICATION_URL}/GetCategories()`
    const headers = buildCopHeaders(token)
    const response = await axios.get<AppNotificationCategoriesResponse>(url, { headers })
    return response.data
  } catch (error) {
    window.console.error('Notification categories API error:', error)
    return null
  }
}

export async function fetchUnreadCount(): Promise<number> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${APP_NOTIFICATION_URL}/GetUnreadCount()`
    const headers = buildCopHeaders(token)
    const response = await axios.get<{ count: number }>(url, { headers })
    return response.data.count ?? 0
  } catch (error) {
    const fallbackMessage = getErrorMessage('notifications-unread-count-loading')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return 0
  }
}
export async function markAllNotificationsAsRead(): Promise<boolean> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${APP_NOTIFICATION_URL}/MarkAllAsRead`
    const headers = buildCopHeaders(token)
    const markAsReadAfter = new Date().toISOString().split('.')[0] + 'Z'
    await axios.post(url, { 'markAsReadAfter': markAsReadAfter }, { headers })
    return true
  } catch (error) {
    const fallbackMessage = getErrorMessage('notifications-mark-all-read')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return false
  }
}

export async function markAsRead(appNotificationId: string): Promise<boolean> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${APP_NOTIFICATION_URL}/MarkAsRead`
    const headers = buildCopHeaders(token)
    await axios.post(url, { appNotificationId }, { headers })
    return true
  } catch (error) {
    const fallbackMessage = getErrorMessage('notifications-mark-read')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return false
  }
}

export async function fetchNotifications(
  params: NotificationListParams,
): Promise<NotificationListResponse | null> {
  const { category, top = 10, skip = 0, unreadOnly } = params

  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${APP_NOTIFICATION_URL}/GetList(appNotificationCategory='${category}')${buildODataQuery(top, skip, unreadOnly)}`
    const headers = buildCopHeaders(token)

    const response = await axios.get<NotificationListResponse>(url, { headers })
    const { '@odata.count': odataCount, value } = response.data
    return { '@odata.count': odataCount, value }
  } catch (error) {
    const fallbackMessage = getErrorMessage('notifications-loading')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function negotiateSignalR(): Promise<SignalRNegotiateResponse | null> {
  try {
    const currentToken = getSilentToken()

    // Step 1: Exchange the current token
    const exchangeResponse = await axios.post<{ token: string }>(
      `${COP_API_BASE_URL}${TOKEN_EXCHANGE_URL}`,
      { token: currentToken },
      { headers: buildCopHeaders(currentToken) }
    )
    const exchangedToken = exchangeResponse.data.token

    // Step 2: Use the exchanged token for SignalR negotiation
    const url = `${COP_API_BASE_URL}${SIGNALR_NEGOTIATE_URL}?negotiateVersion=1`
    const headers = buildCopHeaders(exchangedToken)
    const response = await axios.post<SignalRNegotiateResponse>(url, null, { headers })
    return response.data
  } catch (error) {
    const fallbackMessage = getErrorMessage('signalr-negotiation')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function acceptCase(caseId: string): Promise<boolean> {
  try {
    const token = getSilentToken()
    const formData = new FormData()
    formData.append('caseId', caseId)
    const url = `${COP_API_BASE_URL}${APPROVE_CASE_ENDPOINT}`;
    await axios.post(url, formData, {
      headers: {
        ...buildCopHeaders(token),
        'Content-Type': 'multipart/form-data',
      },
    })
    return true
  } catch (error) {
    const fallbackMessage = getErrorMessage('case-accepting')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return false
  }
}

export async function rejectCase(caseId: string, reason: string): Promise<boolean> {
  try {
    const token = getSilentToken()
    const formData = new FormData()
    formData.append('caseId', caseId)
    formData.append('reason', reason)
    const url = `${COP_API_BASE_URL}${REJECT_CASE_ENDPOINT}`;
    await axios.post(url, formData, {
      headers: {
        ...buildCopHeaders(token),
        'Content-Type': 'multipart/form-data',
      },
    })
    return true
  } catch (error) {
    const fallbackMessage = getErrorMessage('case-rejecting')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return false
  }
}

